import { useMemo, useState } from "react";
import L from "leaflet";
import { Marker, Popup, useMapEvents } from "react-leaflet";
import { formatPrice, formatPricePerM2 } from "./sale";

// From this zoom, homes show their asking price; further out, hundreds of
// price tags would pile up, so they show as small houses
const PRICE_TAG_ZOOM = 14;

const HOUSE_SVG =
  '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5 1.5 7.2V15h4.6v-4.4h3.8V15h4.6V7.2Z"/></svg>';

const houseIcon = (approximate) =>
  L.divIcon({
    className: "aq-sale-anchor",
    html: `<span class="aq-sale-house${approximate ? " is-approx" : ""}">${HOUSE_SVG}</span>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -10],
  });

const HOUSE = houseIcon(false);
const HOUSE_APPROX = houseIcon(true);

// "79,9" for 79.900.000 kr.; "–" when the price is on request
const shortPrice = (kr) =>
  kr == null
    ? "–"
    : (kr / 1e6).toLocaleString("is-IS", { maximumFractionDigits: 1 });

// A price tag with a pointer at the home's position. The anchor is the
// pointer's tip; CSS centres the tag above it, so it fits any price width.
const priceIcon = (listing) =>
  L.divIcon({
    className: "aq-sale-anchor",
    html: `<span class="aq-sale-tag${listing.approximate ? " is-approx" : ""}">${shortPrice(listing.price)}</span>`,
    iconSize: null,
    iconAnchor: [0, 0],
    popupAnchor: [0, -30],
  });

const format = (n, unit) =>
  n == null ? null : `${n.toLocaleString("is-IS")} ${unit}`;

const ListingPopup = ({ listing }) => {
  const perM2 = formatPricePerM2(listing);
  return (
    <Popup pane="popupPane" closeButton={false} minWidth={260} maxWidth={300}>
      <div className="aq-popup aq-popup--sale">
        {listing.image && (
          <img
            className="aq-popup__photo"
            src={listing.image}
            alt=""
            loading="lazy"
          />
        )}
        <p className="aq-popup__level">{listing.type}</p>
        <h3 className="aq-popup__name">
          {listing.address}, {listing.postcode}
        </h3>
        <p className="aq-popup__price">{formatPrice(listing.price)}</p>
        <p className="aq-popup__wind">
          {[
            format(listing.sizeM2, "m²"),
            listing.rooms != null && `${listing.rooms} rooms`,
            perM2,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        {listing.approximate && (
          <p className="aq-popup__wind">
            Placed on the street; the exact address isn't listed.
          </p>
        )}
        <p className="aq-popup__links">
          <a href={listing.url} target="_blank" rel="noreferrer">
            Open on fasteignir.is
          </a>
        </p>
      </div>
    </Popup>
  );
};

export const SaleListings = ({ listings }) => {
  const map = useMapEvents({ zoomend: () => setZoom(map.getZoom()) });
  const [zoom, setZoom] = useState(() => map.getZoom());
  const tags = zoom >= PRICE_TAG_ZOOM;

  // Price tags differ per listing, so build them once per listing set
  const priceIcons = useMemo(
    () => new Map(listings.map((l) => [l.id, priceIcon(l)])),
    [listings],
  );

  return listings.map((listing) => (
    <Marker
      key={listing.id}
      position={[listing.lat, listing.lon]}
      icon={
        tags
          ? priceIcons.get(listing.id)
          : listing.approximate
            ? HOUSE_APPROX
            : HOUSE
      }
      title={`${listing.address}, ${formatPrice(listing.price)}`}
    >
      <ListingPopup listing={listing} />
    </Marker>
  ));
};
